import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { MessageSquare, Send, Plus, Trash2, Edit3, X, AlertTriangle, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { toast } from "sonner";

/** Fill Close-style Liquid variables using the current client + org context. */
function renderTemplate(text, ctx) {
  if (!text) return "";
  const map = {
    "contact.first_name": ctx.first_name || (ctx.name || "").split(" ")[0] || "there",
    "contact.last_name": ctx.last_name || ((ctx.name || "").split(" ").slice(1).join(" ")) || "",
    "contact.name": ctx.name || "",
    "user.first_name": ctx.actor_first_name || "",
    "user.name": ctx.actor_name || "",
    "organization.name": "Credit Quack",
  };
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, k) => (k in map ? map[k] : m));
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function SendSMSDialog({ client, actor, onSent }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [thread, setThread] = useState({ items: [], error: null });
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [text, setText] = useState("");
  const [fromNumber, setFromNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [templateEditor, setTemplateEditor] = useState(null); // {id?, name, text} or null
  const [tab, setTab] = useState("compose"); // compose | thread | templates

  const load = async () => {
    try {
      const [s, t, th] = await Promise.all([
        api.get("/close-sms/status"),
        api.get("/close-sms/templates"),
        api.get(`/close-sms/clients/${client.id}/thread`),
      ]);
      setStatus(s.data);
      setTemplates(t.data.templates || []);
      setThread(th.data);
      if (s.data?.numbers?.length && !fromNumber) {
        const def = s.data.numbers.find((n) => n.is_default) || s.data.numbers[0];
        setFromNumber(def.number);
      }
    } catch (e) {
      setStatus({ configured: false, error: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const applyTemplate = (tid) => {
    if (tid === "__create__") {
      // Inline shortcut: jump straight into the template editor (shared with the whole team)
      setTab("templates");
      setTemplateEditor({ name: "", text: "" });
      return;
    }
    setSelectedTemplateId(tid);
    const tpl = templates.find((t) => t.id === tid);
    if (tpl) {
      const rendered = renderTemplate(tpl.text, {
        name: client.name,
        first_name: (client.name || "").split(" ")[0],
        last_name: (client.name || "").split(" ").slice(1).join(" "),
        actor_first_name: actor?.first_name || (actor?.name || "").split(" ")[0],
        actor_name: actor?.name,
      });
      setText(rendered);
    }
  };

  const send = async () => {
    if (!text.trim()) return toast.error("Message can't be empty");
    setBusy(true);
    try {
      await api.post("/close-sms/send", {
        client_id: client.id,
        text: text.trim(),
        from_number: fromNumber || undefined,
      });
      toast.success(`SMS sent to ${client.name}`);
      setText("");
      setSelectedTemplateId("");
      await load();
      setTab("thread");
      onSent && onSent();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(detail, { duration: 8000, description: "SMS did not send." });
    } finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    if (!templateEditor?.name?.trim() || !templateEditor?.text?.trim()) return toast.error("Name and body required");
    setBusy(true);
    try {
      if (templateEditor.id) {
        await api.put(`/close-sms/templates/${templateEditor.id}`, {
          name: templateEditor.name.trim(), text: templateEditor.text,
        });
      } else {
        await api.post("/close-sms/templates", {
          name: templateEditor.name.trim(), text: templateEditor.text,
        });
      }
      toast.success("Template saved");
      setTemplateEditor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save template");
    } finally { setBusy(false); }
  };

  const deleteTemplate = async (tid) => {
    if (!confirm("Delete this template?")) return;
    try {
      await api.delete(`/close-sms/templates/${tid}`);
      toast.success("Template deleted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const closeTemplates = templates.filter((t) => t.source === "close");
  const localTemplates = templates.filter((t) => t.source === "local");
  const charCount = text.length;
  const smsSegments = Math.max(1, Math.ceil(charCount / 160));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="send-sms-btn">
          <MessageSquare size={16} className="mr-1" /> Text
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sms-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <MessageSquare className="text-emerald-600" size={22} /> Text {client?.name}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Sends through your Close account · logs to the lead's Close timeline automatically.
            {status?.org_name && <> · <span className="font-semibold">{status.org_name}</span></>}
          </p>
        </DialogHeader>

        {status && !status.configured && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2 items-start" data-testid="sms-config-error">
            <AlertTriangle size={16} className="mt-0.5" />
            <div>
              <p className="font-semibold">Close SMS not ready</p>
              <p className="text-xs mt-0.5">{status.error}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1">
          {[
            { v: "compose", label: "Compose" },
            { v: "thread", label: `Thread${thread?.items?.length ? " · " + thread.items.length : ""}` },
            { v: "templates", label: `Templates · ${templates.length}` },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => setTab(b.v)}
              className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${tab === b.v ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
              data-testid={`sms-tab-${b.v}`}
            >{b.label}</button>
          ))}
        </div>

        {tab === "compose" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>From (Close number)</Label>
                <Select value={fromNumber} onValueChange={setFromNumber}>
                  <SelectTrigger data-testid="sms-from-number"><SelectValue placeholder="Pick number" /></SelectTrigger>
                  <SelectContent>
                    {(status?.numbers || []).map((n) => (
                      <SelectItem key={n.number} value={n.number}>{n.number}{n.is_default ? " · default" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Input value={client?.phone || "— no phone on file —"} disabled className="font-mono" />
              </div>
            </div>

            <div>
              <Label>Template</Label>
              <Select value={selectedTemplateId} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="sms-template-picker"><SelectValue placeholder={`${templates.length} templates — pick one to prefill`} /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__create__" className="text-amber-700 font-semibold" data-testid="sms-template-create">
                    ＋ Create new template…
                  </SelectItem>
                  {closeTemplates.length > 0 && (
                    <>
                      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-slate-400">From Close</div>
                      {closeTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </>
                  )}
                  {localTemplates.length > 0 && (
                    <>
                      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-widest font-bold text-amber-600">Client Quack Templates</div>
                      {localTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <span className="text-xs text-slate-400" data-testid="sms-char-count">{charCount} chars · {smsSegments} segment{smsSegments > 1 ? "s" : ""}</span>
              </div>
              <Textarea
                rows={6}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`Hi ${(client?.name || "").split(" ")[0] || "there"} — quick update…`}
                className="font-mono text-sm"
                data-testid="sms-text"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Reminder: A2P/10DLC rules require prior consent + a way to opt-out. Add "Reply STOP to opt out" if this is a marketing-style blast.
              </p>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={send}
                disabled={busy || !text.trim() || !client?.phone || !status?.configured}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="sms-send-btn"
              >
                <Send size={14} className="mr-1" /> {busy ? "Sending…" : "Send via Close"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {tab === "thread" && (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {thread.error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="sms-thread-error">
                {thread.error}
              </div>
            )}
            {thread.items?.length === 0 && !thread.error && (
              <p className="text-sm text-slate-400 text-center py-8">No SMS history yet with {client?.name}.</p>
            )}
            {thread.items?.map((m) => {
              const outbound = m.direction === "outbound";
              return (
                <div key={m.id} className={`rounded-xl border p-3 ${outbound ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-100"}`} data-testid={`sms-msg-${m.id}`}>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                    {outbound ? <ArrowUpRight size={12} className="text-emerald-600" /> : <ArrowDownLeft size={12} className="text-slate-500" />}
                    <span className="font-semibold">{outbound ? m.from : m.to}</span>
                    <span>→</span>
                    <span>{outbound ? m.to : m.from}</span>
                    <span className="ml-auto">{fmt(m.date_sent || m.date_created)}</span>
                    <Badge className={`${m.status === "sent" ? "bg-emerald-100 text-emerald-700" : m.status === "error" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-600"} border-0 uppercase text-[9px] tracking-widest`}>{m.status}</Badge>
                  </div>
                  <p className="text-sm whitespace-pre-wrap text-slate-800">{m.text}</p>
                  {m.error && <p className="text-xs text-red-600 mt-1">⚠ {m.error}</p>}
                </div>
              );
            })}
          </div>
        )}

        {tab === "templates" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Close-hosted templates ({closeTemplates.length}) are read-only here — edit them in Close.
                Client Quack templates ({localTemplates.length}) are yours to manage.
              </p>
              <Button size="sm" onClick={() => setTemplateEditor({ name: "", text: "" })} className="bg-amber-500 hover:bg-amber-600" data-testid="new-template-btn">
                <Plus size={14} className="mr-1" /> New template
              </Button>
            </div>

            {templateEditor && (
              <Card className="p-4 border-amber-200 border-2 rounded-xl bg-amber-50/40" data-testid="template-editor">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-display font-bold">{templateEditor.id ? "Edit template" : "New template"}</h4>
                  <Button size="sm" variant="ghost" onClick={() => setTemplateEditor(null)} className="ml-auto"><X size={14} /></Button>
                </div>
                <Label>Name</Label>
                <Input value={templateEditor.name} onChange={(e) => setTemplateEditor({ ...templateEditor, name: e.target.value })} data-testid="template-name" />
                <Label className="mt-2 block">Body — use {"{{ contact.first_name }}"}, {"{{ user.first_name }}"}, {"{{ organization.name }}"}</Label>
                <Textarea rows={5} value={templateEditor.text} onChange={(e) => setTemplateEditor({ ...templateEditor, text: e.target.value })} className="font-mono text-sm" data-testid="template-text" />
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="ghost" onClick={() => setTemplateEditor(null)}>Cancel</Button>
                  <Button onClick={saveTemplate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="template-save">Save</Button>
                </div>
              </Card>
            )}

            <div className="space-y-2">
              {templates.map((t) => (
                <Card key={t.id} className="p-3 rounded-xl border-slate-100" data-testid={`template-${t.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{t.name}</p>
                        <Badge className={`${t.source === "close" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"} border-0 uppercase text-[9px] tracking-widest`}>{t.source}</Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap line-clamp-3">{t.text}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setTab("compose"); applyTemplate(t.id); }} data-testid={`template-use-${t.id}`} title="Use">
                        <Send size={12} />
                      </Button>
                      {t.source === "local" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setTemplateEditor({ id: t.id, name: t.name, text: t.text })} data-testid={`template-edit-${t.id}`}>
                            <Edit3 size={12} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.id)} className="text-red-500" data-testid={`template-delete-${t.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
